import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Spinner from "@/components/ui/Spinner";

const FiveColumnTable = ({
  data,
  column1Key,
  column2Key,
  column3Key,
  column4Key,
  column5Key,
  column1Title,
  column2Title,
  column3Title,
  column4Title,
  column5Title,
  title,
  columnRatio = "20-20-20-20-20",
  columnAlignment = "l-l-l-l-l",
  isLoaded = false,
  error = null,
  boxHeight = "400px",
  minCellHeight = "2.5rem",
  pagination = false,
  noDataMessage,
}) => {
  const { t } = useTranslation();
  const resolvedNoDataMessage =
    noDataMessage ?? t("common.noDataGeneric", "No data available");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Parse column ratio to get percentage widths
  const [col1Width, col2Width, col3Width, col4Width, col5Width] = columnRatio
    .split("-")
    .map(Number);

  // Parse column alignment
  const [col1Align, col2Align, col3Align, col4Align, col5Align] =
    columnAlignment.split("-");
  const getAlignmentClass = (align) => {
    switch (align) {
      case "l":
        return "text-start";
      case "r":
        return "text-end";
      case "c":
        return "text-center";
      default:
        return "text-start";
    }
  };

  // Calculate pagination values (only used when pagination is true)
  const totalPages = Math.ceil(data.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = pagination ? data.slice(startIndex, endIndex) : data;

  // Navigation handlers
  const goToPreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  // Helper function to render cell content
  const renderCellContent = (content, alignment) => {
    if (React.isValidElement(content)) {
      return <div className={getAlignmentClass(alignment)}>{content}</div>;
    }

    // Check if content is a string (HTML or plain text)
    if (typeof content === "string") {
      return (
        <div
          className={`text-truncate ${getAlignmentClass(alignment)}`}
          dangerouslySetInnerHTML={{ __html: content }}
        />
      );
    }

    // Check if content is an array (for domains, name variations, etc.)
    if (Array.isArray(content)) {
      return (
        <div className={`text-truncate ${getAlignmentClass(alignment)}`}>
          {content.join(", ")}
        </div>
      );
    }

    // For any other type, convert to string
    return (
      <div className={`text-truncate ${getAlignmentClass(alignment)}`}>
        {content != null ? String(content) : ""}
      </div>
    );
  };

  // Handle error state
  if (error) {
    return (
      <div
        className="bg-white card-radius border"
        style={{ borderColor: "var(--border)" }}
      >
        {title && (
          <div className="px-3 py-2 border-bottom bg-light">
            <div className="text-sm fw-medium text-dark">{title}</div>
          </div>
        )}
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: boxHeight }}
        >
          <div className="w-100 text-center align-self-start m-3">
            <div className="alert alert-danger mb-0" role="alert">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {t(
                "common.loadError",
                "An error occurred while loading the data. Please try again later.",
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle loading state
  if (!isLoaded) {
    return (
      <div
        className="bg-white card-radius border"
        style={{ borderColor: "var(--border)" }}
      >
        {title && (
          <div className="px-3 py-2 border-bottom bg-light">
            <div className="text-sm fw-medium text-dark">{title}</div>
          </div>
        )}
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: boxHeight }}
        >
          <Spinner />
        </div>
      </div>
    );
  }

  // Handle no data state
  if (isLoaded && data.length === 0) {
    return (
      <div
        className="bg-white card-radius border"
        style={{ borderColor: "var(--border)" }}
      >
        {title && (
          <div className="px-3 py-2 border-bottom bg-light">
            <div className="text-sm fw-medium text-dark">{title}</div>
          </div>
        )}
        <div
          className="d-flex justify-content-center align-items-center text-muted"
          style={{ height: boxHeight }}
        >
          <div>
            <i className="bi bi-table me-2"></i>
            {resolvedNoDataMessage}
          </div>
        </div>
      </div>
    );
  }

  // Handle loaded data state
  return (
    <div
      className="bg-white card-radius border"
      style={{ borderColor: "var(--border)" }}
    >
      {title && (
        <div className="px-3 py-2 border-bottom bg-light">
          <div className="text-sm fw-medium text-dark">{title}</div>
        </div>
      )}

      {/* Table Header */}
      <div className="px-3 py-2 border-bottom bg-light">
        <div className="row g-0">
          <div style={{ width: `${col1Width}%` }}>
            <div
              className={`text-sm fw-medium text-dark ${getAlignmentClass(
                col1Align,
              )}`}
            >
              {column1Title}
            </div>
          </div>
          <div style={{ width: `${col2Width}%` }}>
            <div
              className={`text-sm fw-medium text-dark ${getAlignmentClass(
                col2Align,
              )}`}
            >
              {column2Title}
            </div>
          </div>
          <div style={{ width: `${col3Width}%` }}>
            <div
              className={`text-sm fw-medium text-dark ${getAlignmentClass(
                col3Align,
              )}`}
            >
              {column3Title}
            </div>
          </div>
          <div style={{ width: `${col4Width}%` }}>
            <div
              className={`text-sm fw-medium text-dark ${getAlignmentClass(
                col4Align,
              )}`}
            >
              {column4Title}
            </div>
          </div>
          <div style={{ width: `${col5Width}%` }}>
            <div
              className={`text-sm fw-medium text-dark ${getAlignmentClass(
                col5Align,
              )}`}
            >
              {column5Title}
            </div>
          </div>
        </div>
      </div>

      {/* Table Body */}
      <div
        style={{
          height: pagination ? "auto" : boxHeight,
          overflowY: pagination ? "visible" : "auto",
        }}
      >
        {/* Render actual data rows */}
        {currentData.map((item, index) => (
          <div
            key={item.id || `item-${pagination ? startIndex + index : index}`}
            className="px-3 py-2 text-sm hoverable-row border-bottom"
            style={{ minHeight: minCellHeight }}
          >
            <div className="row g-0 align-items-center">
              <div style={{ width: `${col1Width}%` }}>
                {renderCellContent(item[column1Key], col1Align)}
              </div>
              <div style={{ width: `${col2Width}%` }}>
                {renderCellContent(item[column2Key], col2Align)}
              </div>
              <div style={{ width: `${col3Width}%` }}>
                {renderCellContent(item[column3Key], col3Align)}
              </div>
              <div style={{ width: `${col4Width}%` }}>
                {renderCellContent(item[column4Key], col4Align)}
              </div>
              <div style={{ width: `${col5Width}%` }}>
                {renderCellContent(item[column5Key], col5Align)}
              </div>
            </div>
          </div>
        ))}

        {/* Render empty placeholder rows to maintain constant height (only for pagination) */}
        {pagination &&
          Array.from(
            { length: itemsPerPage - currentData.length },
            (_, index) => (
              <div
                key={`empty-${index}`}
                className="px-3 py-2 text-sm"
                style={{ minHeight: minCellHeight }}
              >
                <div className="row g-0">
                  <div style={{ width: `${col1Width}%` }}>
                    <div
                      className={`text-truncate ${getAlignmentClass(col1Align)}`}
                    >
                      &nbsp;
                    </div>
                  </div>
                  <div style={{ width: `${col2Width}%` }}>
                    <div
                      className={`text-truncate ${getAlignmentClass(col2Align)}`}
                    >
                      &nbsp;
                    </div>
                  </div>
                  <div style={{ width: `${col3Width}%` }}>
                    <div
                      className={`text-truncate ${getAlignmentClass(col3Align)}`}
                    >
                      &nbsp;
                    </div>
                  </div>
                  <div style={{ width: `${col4Width}%` }}>
                    <div
                      className={`text-truncate ${getAlignmentClass(col4Align)}`}
                    >
                      &nbsp;
                    </div>
                  </div>
                  <div style={{ width: `${col5Width}%` }}>
                    <div
                      className={`text-truncate ${getAlignmentClass(col5Align)}`}
                    >
                      &nbsp;
                    </div>
                  </div>
                </div>
              </div>
            ),
          )}
      </div>

      {/* Table Footer with Pagination (only show when pagination is true) */}
      {pagination && (
        <div className="px-3 py-2 border-top bg-light">
          <div className="d-flex justify-content-center align-items-center">
            <button
              className="btn btn-sm btn-outline-secondary me-3"
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              style={{ minWidth: "30px" }}
            >
              &lt;
            </button>

            <span className="text-sm text-muted">
              {t("common.pageOf", {
                current: currentPage,
                total: totalPages,
                defaultValue: `Page ${currentPage} of ${totalPages}`,
              })}
            </span>

            <button
              className="btn btn-sm btn-outline-secondary ms-3"
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              style={{ minWidth: "30px" }}
            >
              &gt;
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FiveColumnTable;
